import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './CatThemeOverlay.css';

const TOTAL_MS = 1150;
const MIDPOINT_MS = Math.round(TOTAL_MS * 0.50); // тема меняется в середине

const PARTICLE_COLORS = ['#ff85a9', '#3a9d6a', '#c084fc', '#ffd060', '#ff6b9d', '#a7f3c8'];

// Мини-лапка и цветок вместо эмодзи. Размер = 1em (управляется fontSize),
// цвет = currentColor (задаётся на частице).
const PawMini = () => (
  <svg viewBox="0 0 32 30" width="1em" height="1em" style={{ display: 'block' }}>
    <ellipse cx="16" cy="21" rx="8" ry="6.5" fill="currentColor" />
    <circle cx="6.5" cy="13" r="3" fill="currentColor" />
    <circle cx="12.5" cy="8.5" r="3.1" fill="currentColor" />
    <circle cx="19.5" cy="8.5" r="3.1" fill="currentColor" />
    <circle cx="25.5" cy="13" r="3" fill="currentColor" />
  </svg>
);

const Petal = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" style={{ display: 'block' }}>
    <g fill="currentColor">
      <ellipse cx="12" cy="5.5" rx="2.8" ry="4.8" />
      <ellipse cx="12" cy="5.5" rx="2.8" ry="4.8" transform="rotate(72 12 12)" />
      <ellipse cx="12" cy="5.5" rx="2.8" ry="4.8" transform="rotate(144 12 12)" />
      <ellipse cx="12" cy="5.5" rx="2.8" ry="4.8" transform="rotate(216 12 12)" />
      <ellipse cx="12" cy="5.5" rx="2.8" ry="4.8" transform="rotate(288 12 12)" />
    </g>
    <circle cx="12" cy="12" r="2.4" fill="#ffd060" />
  </svg>
);

// Частицы детерминированы — вычисляются один раз при загрузке модуля
const PARTICLES = (() => {
  const COUNT = 22;
  return Array.from({ length: COUNT }, (_, i) => {
    const angle = (i / COUNT) * 2 * Math.PI;
    const dist = 130 + (i % 4) * 55;
    // Чередуем: лапка / искра / цветок
    const kind = i % 3 === 0 ? 'paw' : (i % 3 === 1 ? 'spark' : 'petal');
    return {
      id: i,
      kind,
      glyph: i % 2 ? '✧' : '✦',
      color: PARTICLE_COLORS[i % PARTICLE_COLORS.length],
      tx: Math.cos(angle) * dist,
      ty: Math.sin(angle) * dist - 24,
      delay: i * 0.022,
      size: 15 + (i % 4) * 4,
    };
  });
})();

function renderParticle(p) {
  if (p.kind === 'paw') return <PawMini />;
  if (p.kind === 'petal') return <Petal />;
  return p.glyph;
}

export default function CatThemeOverlay({ isRunning, origin, entering, onMidpoint, onDone }) {
  const timersRef = useRef([]);

  useEffect(() => {
    // Сброс при размонтировании / повторном запуске
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!isRunning) return;

    // Очищаем старые таймеры
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    const t1 = setTimeout(onMidpoint, MIDPOINT_MS);
    const t2 = setTimeout(onDone, TOTAL_MS + 50);

    timersRef.current = [t1, t2];
  }, [isRunning]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isRunning) return null;

  const { x, y } = origin;
  const waveStyle = {
    '--cg-ox': `${x}px`,
    '--cg-oy': `${y}px`,
  };

  return ReactDOM.createPortal(
    <div className="cg-overlay-root">
      {/* Основная волна — анимация на чистом CSS */}
      <div
        className={`cg-wave ${entering ? 'cg-wave--to-cat' : 'cg-wave--to-normal'}`}
        style={waveStyle}
      >
        <div className="cg-ring cg-ring--1" />
        <div className="cg-ring cg-ring--2" />
        <div className="cg-ring cg-ring--3" />
        <div className="cg-ring-sparkles">✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦ ✧ ✦</div>
        <div className="cg-wave__center">
          {entering
            ? <img src="/cat-girl-peek.png" className="cg-wave__cat" alt="" draggable="false" />
            : <span className="cg-wave__spark">✦</span>}
        </div>
      </div>

      {/* Разлетающиеся частицы — framer-motion */}
      <AnimatePresence>
        {PARTICLES.map(p => (
          <motion.div
            key={p.id}
            className="cg-particle"
            style={{ left: x, top: y, fontSize: p.size, color: p.color }}
            initial={{ x: 0, y: 0, scale: 0, opacity: 0 }}
            animate={{
              x: p.tx,
              y: p.ty,
              scale: [0, 1.5, 0.9, 0],
              opacity: [0, 1, 0.85, 0],
            }}
            transition={{
              duration: 0.95,
              delay: p.delay,
              ease: 'easeOut',
              opacity: { times: [0, 0.12, 0.7, 1] },
              scale:   { times: [0, 0.18, 0.65, 1] },
            }}
          >
            {renderParticle(p)}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
}
